import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Expander, Text } from '~/components/core';
import { useStyles } from '~/hooks';
import i18n from '~/i18n';
import { formatHms } from '~/lib/format';
import { FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { CreateStrategyWizardApi } from '../../../hooks/useCreateStrategyWizard';
import SummaryGroup from '../SummaryGroup';

type Props = {
    wizard: CreateStrategyWizardApi;
};

// Final step: grouped human-readable review with per-group Edit jumps. Strategies have no name,
// so the Market group leads with the instrument/period/side summary.
export default function StepReview({ wizard }: Props) {
    const styles = useStyles(stylesGenerator);

    // Only built for accounts with strategyRawEnabled — an opt-in support/debug view.
    const payloadJson = useMemo(() => {
        if (!wizard.rawEnabled) {
            return null;
        }
        const payload = wizard.buildPayload();
        return payload ? JSON.stringify(payload, null, 2) : null;
    }, [wizard]);

    return (
        <View>
            <SummaryGroup
                title="ai-strategy-step-market"
                lines={[wizard.summaries.market]}
                onEdit={() => wizard.goToStep('market')}
            />
            <SummaryGroup
                title="ai-strategy-step-trade"
                lines={[wizard.summaries.trade]}
                onEdit={() => wizard.goToStep('trade')}
            />
            <SummaryGroup
                title="ai-strategy-step-rules"
                lines={[wizard.summaries.rules || i18n.t('ai-strategy-rule-incomplete')]}
                onEdit={() => wizard.goToStep('rules')}
            />
            {/* Nothing to review — or edit — when the account skips the optimisation step. */}
            {wizard.optimisationsEnabled ? (
                <SummaryGroup
                    title="ai-strategy-step-optimisation"
                    lines={[wizard.summaries.optimisationText]}
                    onEdit={() => wizard.goToStep('optimisation')}
                />
            ) : null}

            {payloadJson ? (
                <View style={styles.payloadBlock}>
                    <Expander title="ai-strategy-payload-title" subtitle={i18n.t('ai-strategy-payload-hint')}>
                        <Text noTranslate style={styles.payloadJson}>
                            {payloadJson}
                        </Text>
                    </Expander>
                </View>
            ) : null}

            {wizard.submitError ? (
                <View style={styles.errorBlock}>
                    <Text noTranslate style={styles.errorText}>
                        {wizard.submitError.message}
                    </Text>
                    {wizard.submitError.step !== undefined ? (
                        <Pressable onPress={() => wizard.setStep(wizard.submitError!.step as number)}>
                            <Text fontType="LATO_BOLD" style={styles.errorLink}>
                                ai-strategy-fix-in-step
                            </Text>
                        </Pressable>
                    ) : null}
                </View>
            ) : null}

            {wizard.cooldownSeconds > 0 ? (
                <Text noTranslate style={styles.cooldownText}>
                    {`${i18n.t('ai-strategy-cooldown')} ${formatHms(wizard.cooldownSeconds)}`}
                </Text>
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        payloadBlock: {
            marginBottom: scaleSize(8),
        },
        payloadJson: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            fontFamily: 'monospace',
        },
        errorBlock: {
            marginTop: scaleSize(4),
        },
        errorText: {
            fontSize: FONT_SIZE_S,
            color: theme.alert,
        },
        errorLink: {
            fontSize: FONT_SIZE_S,
            color: theme.button,
            marginTop: scaleSize(4),
        },
        cooldownText: {
            fontSize: FONT_SIZE_S,
            color: theme.alert,
            marginTop: scaleSize(8),
            textAlign: 'center',
        },
    });
